import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileDown, Plus, Trash2, Save, Check, Loader2, CalendarDays, X, Link2, Copy, ChevronLeft, ChevronRight, CalendarPlus, AlertTriangle, ArrowLeftFromLine } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: "G", label: "On Track", color: "bg-green-500", text: "text-green-700 dark:text-green-300", bg: "bg-green-50 dark:bg-green-900/40", border: "border-green-300 dark:border-green-600" },
  { value: "Y", label: "At Risk", color: "bg-yellow-500", text: "text-yellow-700 dark:text-yellow-200", bg: "bg-yellow-50 dark:bg-yellow-900/40", border: "border-yellow-300 dark:border-yellow-600" },
  { value: "R", label: "Off Track", color: "bg-red-500", text: "text-red-700 dark:text-red-300", bg: "bg-red-50 dark:bg-red-900/40", border: "border-red-300 dark:border-red-600" },
  { value: "N", label: "Not Started", color: "bg-gray-400", text: "text-gray-700 dark:text-gray-300", bg: "bg-gray-50 dark:bg-gray-800/50", border: "border-gray-300 dark:border-gray-600" },
  { value: "C", label: "Complete", color: "bg-blue-500", text: "text-blue-700 dark:text-blue-300", bg: "bg-blue-50 dark:bg-blue-900/40", border: "border-blue-300 dark:border-blue-600" },
];

const getStatusConfig = (val) => statusOptions.find((s) => s.value === val) || statusOptions[0];

const msStatusMap = { Completed: "C", "On Track": "G", "In Progress": "G", "At Risk": "Y", Delayed: "R", "Off Track": "R", "Not Started": "N", "On Time": "G" };
const fmtDate = (d) => { if (!d) return ""; const p = d.split("-"); return p.length === 3 ? `${p[1]}-${p[2]}-${p[0]}` : d; };

export const QuadChartPanel = () => {
  const { selectedProduct } = useAuth();
  const productId = selectedProduct?.id;
  const [data, setData] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [shareCopied, setShareCopied] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [activeMonth, setActiveMonth] = useState("");
  const [showNewMonthPicker, setShowNewMonthPicker] = useState(false);
  const [targetMonth, setTargetMonth] = useState("");
  const [confirmAction, setConfirmAction] = useState(null); // {type: "create"|"delete", month, label}

  const currentMonth = new Date().toISOString().slice(0, 7); // "YYYY-MM"

  const formatMonthLabel = (ym) => {
    if (!ym) return "";
    const [y, m] = ym.split("-");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[parseInt(m, 10) - 1]} ${y}`;
  };

  const fetchMonths = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/quadchart/months`, { params: { product_id: productId } });
      setAvailableMonths(res.data || []);
      return res.data || [];
    } catch (e) { return []; }
  }, [productId]);

  const fetchChart = useCallback(async (month) => {
    try {
      const params = { product_id: productId };
      if (month) params.report_month = month;
      const [qcRes, msRes, reqRes, shRes] = await Promise.all([
        axios.get(`${API}/quadchart`, { params }),
        axios.get(`${API}/milestones`, { params: { product_id: productId } }),
        axios.get(`${API}/requirements`, { params: { product_id: productId } }),
        axios.get(`${API}/stakeholders`, { params: { product_id: productId } }),
      ]);
      const qcData = qcRes.data;
      if (productId && !qcData.product_id) qcData.product_id = productId;
      if (!qcData.project_name && selectedProduct?.name) qcData.project_name = selectedProduct.name;
      // Normalize highlights and upcoming to plain strings
      if (Array.isArray(qcData.highlights)) qcData.highlights = qcData.highlights.map(h => typeof h === "string" ? h : (h.text || String(h)));
      if (Array.isArray(qcData.upcoming)) qcData.upcoming = qcData.upcoming.map(u => typeof u === "string" ? u : (u.text || String(u)));
      setData(qcData);
      setActiveMonth(qcData.report_month || month || currentMonth);
      setMilestones(msRes.data);
      setRequirements(reqRes.data);
      setStakeholders(shRes.data);
    } catch (e) { console.error(e); }
  }, [productId, currentMonth]);

  useEffect(() => {
    const init = async () => {
      const months = await fetchMonths();
      // Load the latest month or current month
      const latest = months.length > 0 ? months[0] : currentMonth;
      await fetchChart(latest);
    };
    init();
  }, [fetchMonths, fetchChart, currentMonth]);

  const switchMonth = async (month) => {
    setData(null);
    setShareLink("");
    await fetchChart(month);
  };

  const requestCreateMonth = (chosenMonth) => {
    const existing = new Set(availableMonths);
    let candidate = chosenMonth;
    if (!candidate) {
      candidate = currentMonth;
      if (existing.has(candidate)) {
        const d = new Date();
        d.setMonth(d.getMonth() + 1);
        candidate = d.toISOString().slice(0, 7);
      }
    }
    if (existing.has(candidate)) {
      alert(`A report for ${formatMonthLabel(candidate)} already exists. Select a different month.`);
      return;
    }
    setConfirmAction({ type: "create", month: candidate, label: formatMonthLabel(candidate) });
  };

  const createNewMonth = async (chosenMonth) => {
    const existing = new Set(availableMonths);
    let candidate = chosenMonth;
    if (existing.has(candidate)) return;

    // Copy ALL data from the currently displayed chart as a template
    const prev = data || {};
    const newChart = {
      product_id: productId || "",
      report_month: candidate,
      project_name: prev.project_name || selectedProduct?.name || "",
      owner: prev.owner || "",
      report_date: new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }),
      overall_status: prev.overall_status || "G",
      goals: prev.goals || "",
      highlights: (prev.highlights || []).map(h => typeof h === "string" ? h : (h.text || String(h))),
      upcoming: (prev.upcoming || []).map(u => typeof u === "string" ? u : (u.text || String(u))),
      selected_milestones: [...(prev.selected_milestones || [])],
      issues: (prev.issues || []).map(i => ({ ...i })),
    };
    try {
      await axios.put(`${API}/quadchart`, newChart);
      await fetchMonths();
      await fetchChart(candidate);
      setShowNewMonthPicker(false);
      setTargetMonth("");
    } catch (e) { console.error(e); }
  };

  const requestDeleteMonth = () => {
    if (!activeMonth || availableMonths.length <= 1) return;
    setConfirmAction({ type: "delete", month: activeMonth, label: formatMonthLabel(activeMonth) });
  };

  const deleteMonth = async (month) => {
    try {
      await axios.delete(`${API}/quadchart?product_id=${productId}&report_month=${month}`);
      const remaining = availableMonths.filter(m => m !== month);
      await fetchMonths();
      if (remaining.length > 0) {
        await fetchChart(remaining[0]);
      } else {
        setData(null);
        setActiveMonth("");
      }
    } catch (e) { console.error(e); }
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    if (confirmAction.type === "create") {
      await createNewMonth(confirmAction.month);
    } else if (confirmAction.type === "delete") {
      await deleteMonth(confirmAction.month);
    }
    setConfirmAction(null);
  };

  const updateField = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const saveChart = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/quadchart`, data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      await fetchMonths();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const exportPptx = async () => {
    setExporting(true);
    try {
      await axios.put(`${API}/quadchart`, data);
      const params = new URLSearchParams();
      if (productId) params.set("product_id", productId);
      if (activeMonth) params.set("report_month", activeMonth);
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/quadchart/export/pptx?${params}`, "_blank");
    } catch (e) { console.error(e); }
    setExporting(false);
  };

  // Highlights management
  const addHighlight = () => updateField("highlights", [...(data.highlights || []), ""]);
  const updateHighlight = (idx, val) => {
    const arr = [...(data.highlights || [])];
    arr[idx] = val;
    updateField("highlights", arr);
  };
  const removeHighlight = (idx) => updateField("highlights", (data.highlights || []).filter((_, i) => i !== idx));

  // Upcoming management
  const addUpcoming = () => updateField("upcoming", [...(data.upcoming || []), ""]);
  const updateUpcoming = (idx, val) => {
    const arr = [...(data.upcoming || [])];
    arr[idx] = val;
    updateField("upcoming", arr);
  };
  const removeUpcoming = (idx) => updateField("upcoming", (data.upcoming || []).filter((_, i) => i !== idx));

  // Move upcoming activity to highlights
  const moveToHighlights = (idx) => {
    const item = (data.upcoming || [])[idx];
    if (!item) return;
    const newUpcoming = (data.upcoming || []).filter((_, i) => i !== idx);
    const newHighlights = [...(data.highlights || []), item];
    setData((prev) => ({ ...prev, upcoming: newUpcoming, highlights: newHighlights }));
    setSaved(false);
  };

  // Issues management
  const addIssue = () => updateField("issues", [...(data.issues || []), { issue: "", owner: "", mitigation: "", impact: "M", due_date: "" }]);
  const updateIssue = (idx, field, val) => {
    const arr = [...(data.issues || [])];
    arr[idx] = { ...arr[idx], [field]: val };
    updateField("issues", arr);
  };
  const removeIssue = (idx) => updateField("issues", (data.issues || []).filter((_, i) => i !== idx));

  // Milestone selection
  const toggleMilestone = (msId) => {
    const selected = data.selected_milestones || [];
    if (selected.includes(msId)) {
      updateField("selected_milestones", selected.filter((id) => id !== msId));
    } else {
      updateField("selected_milestones", [...selected, msId]);
    }
  };

  // Auto-save after changes (debounced)
  useEffect(() => {
    if (!data) return;
    const timer = setTimeout(async () => {
      try {
        await axios.put(`${API}/quadchart`, data);
        fetchMonths();
      } catch (e) { console.error(e); }
    }, 1500);
    return () => clearTimeout(timer);
  }, [data, fetchMonths]);

  // Completed requirements for highlights
  const completedReqs = requirements.filter((r) => r.status === "Completed");

  const generateShareLink = async () => {
    try {
      const res = await axios.post(`${API}/quadchart/share`, { product_id: productId || "", report_month: activeMonth });
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/share/quadchart/${res.data.token}`;
      setShareLink(link);
    } catch (e) { console.error(e); }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  if (!data) return <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const statusCfg = getStatusConfig(data.overall_status);
  const selectedMs = milestones.filter((ms) => (data.selected_milestones || []).includes(ms.id));

  return (
    <div data-testid="quadchart-panel">
      <p className="text-xs font-semibold tracking-widest uppercase text-[#00D4C8] mb-3">Reporting</p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit']">Quad Chart</h1>
      <p className="text-base text-slate-500 dark:text-slate-400 mt-2">Interactive project status report</p>
      <Separator className="my-6 bg-slate-200 dark:bg-slate-700" />

      {/* ─── Month Selector Bar ─── */}
      <div className="mb-6" data-testid="month-selector">
        <div className="flex items-center gap-2 flex-wrap">
          {availableMonths.map((m) => (
            <button
              key={m}
              onClick={() => switchMonth(m)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${
                m === activeMonth
                  ? "bg-[#0F172A] text-white border-[#0F172A] dark:bg-[#00D4C8] dark:border-[#00D4C8] dark:text-slate-900 shadow-md"
                  : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-400 hover:shadow-sm"
              }`}
              data-testid={`month-btn-${m}`}
            >
              <CalendarDays className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              {formatMonthLabel(m)}
            </button>
          ))}
          {availableMonths.length > 1 && activeMonth && (
            <button
              onClick={requestDeleteMonth}
              className="p-2 rounded-lg border border-red-200 dark:border-red-800 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:border-red-300 dark:hover:border-red-600 transition-all"
              title={`Delete ${formatMonthLabel(activeMonth)}`}
              data-testid="delete-month-btn"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {availableMonths.length === 0 && (
            <span className="text-sm text-slate-400 dark:text-slate-500">No saved reports yet</span>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3 mb-6">
        <Button onClick={saveChart} disabled={saving} className="bg-[#0F172A] hover:bg-slate-800 text-white" data-testid="quadchart-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : saved ? <Check className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? "Saving..." : saved ? "Saved!" : "Save"}
        </Button>
        <Button onClick={exportPptx} disabled={exporting} variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="quadchart-export-btn">
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
          Export PowerPoint
        </Button>
        <Button onClick={generateShareLink} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-900/30" data-testid="quadchart-share-btn">
          <Link2 className="w-4 h-4 mr-2" />
          Share Link
        </Button>
      </div>

      {/* Share Link Display */}
      {shareLink && (
        <div className="flex items-center gap-2 mb-6 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800" data-testid="share-link-bar">
          <Link2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <input readOnly value={shareLink} className="flex-1 text-sm bg-transparent border-none outline-none text-blue-700 dark:text-blue-300 font-mono" />
          <Button size="sm" onClick={copyShareLink} variant="ghost" className="text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/40 h-8 px-3" data-testid="copy-share-link">
            {shareCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            <span className="ml-1 text-xs">{shareCopied ? "Copied!" : "Copy"}</span>
          </Button>
        </div>
      )}

      {/* ─── Header Section ─── */}
      <Card className="border border-slate-200 shadow-sm bg-white mb-4" data-testid="quadchart-header">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-5">
              <label className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-1 block">Project Name</label>
              <Input value={data.project_name || ""} onChange={(e) => updateField("project_name", e.target.value)} className="font-semibold" data-testid="qc-project-name" />
            </div>
            <div className="md:col-span-3">
              <label className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-1 block">Owner</label>
              <Select value={data.owner || ""} onValueChange={(val) => updateField("owner", val)}>
                <SelectTrigger data-testid="qc-owner"><SelectValue placeholder="Select IT stakeholder" /></SelectTrigger>
                <SelectContent>
                  {stakeholders.filter(s => s.type === "it" || s.type === "IT").map((s) => (
                    <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                  ))}
                  {stakeholders.filter(s => s.type === "it" || s.type === "IT").length === 0 && (
                    <SelectItem value="__none" disabled>No IT stakeholders found</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-1 block">Report Date</label>
              <input type="date" value={data.report_date || ""} onChange={(e) => updateField("report_date", e.target.value)}
                className="h-9 w-full px-3 rounded-md text-sm border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500/30" data-testid="qc-report-date" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-1 block">Overall Status</label>
              <Select value={data.overall_status || "G"} onValueChange={(val) => updateField("overall_status", val)}>
                <SelectTrigger data-testid="qc-overall-status" className={`${statusCfg.bg} ${statusCfg.text} ${statusCfg.border} border font-semibold`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${s.color}`} />
                        {s.value} - {s.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ─── Quad Grid ─── */}
      {/* Q1: Project Description/Goals - FULL WIDTH */}
      <Card className="border border-slate-200 shadow-sm bg-white mb-4" data-testid="qc-goals-section">
        <div className="bg-teal-600 px-5 py-2.5 rounded-t-lg">
          <h3 className="text-sm font-bold text-white tracking-wide">Project Description/Goals</h3>
        </div>
        <CardContent className="p-5">
          <Textarea
            value={data.goals || ""}
            onChange={(e) => updateField("goals", e.target.value)}
            rows={10}
            placeholder="Enter project goals and descriptions..."
            className="text-sm leading-relaxed resize-y min-h-[160px]"
            data-testid="qc-goals-text"
          />
        </CardContent>
      </Card>

      {/* Q2: Milestones - FULL WIDTH */}
      <Card className="border border-slate-200 shadow-sm bg-white mb-4" data-testid="qc-milestones-section">
        <div className="bg-teal-600 px-5 py-2.5 rounded-t-lg">
          <h3 className="text-sm font-bold text-white tracking-wide">Milestones</h3>
        </div>
        <CardContent className="p-4">
          <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mb-3">Select milestones to include</p>
          <div className="space-y-1.5">
            {milestones.map((ms) => {
              const isSelected = (data.selected_milestones || []).includes(ms.id);
              const msSt = msStatusMap[ms.status] || "N";
              const msCfg = getStatusConfig(msSt);
              return (
                <div key={ms.id}
                  onClick={() => toggleMilestone(ms.id)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all text-sm ${isSelected ? "bg-teal-50 border border-teal-200" : "bg-slate-50 border border-transparent hover:bg-slate-100"}`}
                  data-testid={`qc-ms-${ms.id}`}
                >
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? "bg-teal-500 border-teal-500" : "border-slate-300"}`}>
                    {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                  <span className={`flex-1 truncate ${isSelected ? "text-slate-800 font-medium" : "text-slate-500"}`}>{ms.title}</span>
                  <span className="text-[10px] text-slate-400 flex-shrink-0">{fmtDate(ms.target_end)}</span>
                  <div className={`w-5 h-5 rounded-full ${msCfg.color} flex items-center justify-center flex-shrink-0`}>
                    <span className="text-[9px] font-bold text-white">{msSt}</span>
                  </div>
                </div>
              );
            })}
            {milestones.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No milestones. Add them in the Milestones page.</p>}
          </div>
          {selectedMs.length > 0 && (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <table className="w-full text-xs" data-testid="qc-ms-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-1.5 text-slate-500 font-semibold">Milestone</th>
                    <th className="text-center py-1.5 text-slate-500 font-semibold w-20">End Date</th>
                    <th className="text-center py-1.5 text-slate-500 font-semibold w-16">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedMs.map((ms) => {
                    const msSt = msStatusMap[ms.status] || "N";
                    const msCfg = getStatusConfig(msSt);
                    return (
                      <tr key={ms.id} className="border-b border-slate-100">
                        <td className="py-1.5 text-slate-700">{ms.title}</td>
                        <td className="py-1.5 text-slate-500 text-center">{fmtDate(ms.target_end)}</td>
                        <td className="py-1.5 text-center">
                          <div className={`w-5 h-5 rounded-full ${msCfg.color} inline-flex items-center justify-center`}>
                            <span className="text-[9px] font-bold text-white">{msSt}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">

        {/* Q3: Highlights / Major Accomplishments */}
        <Card className="border border-slate-200 shadow-sm bg-white" data-testid="qc-highlights-section">
          <div className="bg-teal-600 px-5 py-2.5 rounded-t-lg">
            <h3 className="text-sm font-bold text-white tracking-wide">Highlights / Major Accomplishments</h3>
          </div>
          <CardContent className="p-4">
            {/* Completed Requirements Quick-Add */}
            {completedReqs.length > 0 && (
              <div className="mb-3 p-3 bg-green-50 border border-green-100 rounded-lg">
                <p className="text-[10px] text-green-700 font-semibold uppercase tracking-wider mb-2">Completed Requirements (click to add)</p>
                <div className="flex flex-wrap gap-1.5">
                  {completedReqs.map((r) => {
                    const alreadyAdded = (data.highlights || []).includes(r.title);
                    return (
                      <button key={r.id}
                        onClick={() => { if (!alreadyAdded) updateField("highlights", [...(data.highlights || []), r.title]); }}
                        disabled={alreadyAdded}
                        className={`px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all ${alreadyAdded ? "bg-green-100 text-green-500 border-green-200 cursor-default" : "bg-white text-green-700 border-green-200 hover:bg-green-100 cursor-pointer"}`}
                        data-testid={`qc-req-${r.id}`}
                      >
                        {alreadyAdded && <Check className="w-2.5 h-2.5 inline mr-1" />}
                        {r.title}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Custom highlights */}
            <div className="space-y-2">
              {(data.highlights || []).map((hl, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-slate-400 text-xs flex-shrink-0">•</span>
                  <Input value={hl} onChange={(e) => updateHighlight(idx, e.target.value)} className="h-8 text-xs flex-1" data-testid={`qc-highlight-${idx}`} />
                  <button onClick={() => removeHighlight(idx)} className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
            <Button onClick={addHighlight} variant="outline" size="sm" className="mt-3 h-7 text-xs" data-testid="qc-add-highlight">
              <Plus className="w-3 h-3 mr-1" />Add Highlight
            </Button>
          </CardContent>
        </Card>

        {/* Q4: Upcoming Activities */}
        <Card className="border border-slate-200 shadow-sm bg-white" data-testid="qc-upcoming-section">
          <div className="bg-teal-600 px-5 py-2.5 rounded-t-lg">
            <h3 className="text-sm font-bold text-white tracking-wide">Upcoming Activities</h3>
          </div>
          <CardContent className="p-4">
            <div className="space-y-2">
              {(data.upcoming || []).map((up, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-slate-400 text-xs flex-shrink-0">•</span>
                  <Input value={up} onChange={(e) => updateUpcoming(idx, e.target.value)} className="h-8 text-xs flex-1" data-testid={`qc-upcoming-${idx}`} />
                  <button onClick={() => moveToHighlights(idx)} className="p-1 rounded text-slate-300 hover:text-teal-600 hover:bg-teal-50 transition-colors" title="Move to Highlights" data-testid={`qc-move-to-highlights-${idx}`}><ArrowLeftFromLine className="w-3 h-3" /></button>
                  <button onClick={() => removeUpcoming(idx)} className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
            <Button onClick={addUpcoming} variant="outline" size="sm" className="mt-3 h-7 text-xs" data-testid="qc-add-upcoming">
              <Plus className="w-3 h-3 mr-1" />Add Activity
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* ─── Issues/Risks Table ─── */}
      <Card className="border border-slate-200 shadow-sm bg-white" data-testid="qc-issues-section">
        <div className="bg-teal-600 px-5 py-2.5 rounded-t-lg">
          <h3 className="text-sm font-bold text-white tracking-wide">Issues / Risks</h3>
        </div>
        <CardContent className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="qc-issues-table">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-2 px-2 text-slate-500 font-semibold w-1/4">Issue / Risk</th>
                  <th className="text-left py-2 px-2 text-slate-500 font-semibold w-28">Owner(s)</th>
                  <th className="text-left py-2 px-2 text-slate-500 font-semibold">Mitigation / Next Steps</th>
                  <th className="text-center py-2 px-2 text-slate-500 font-semibold w-20">Impact</th>
                  <th className="text-center py-2 px-2 text-slate-500 font-semibold w-28">Due Date</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {(data.issues || []).map((issue, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-1.5 px-2">
                      <Input value={issue.issue} onChange={(e) => updateIssue(idx, "issue", e.target.value)} className="h-7 text-xs" data-testid={`qc-issue-text-${idx}`} />
                    </td>
                    <td className="py-1.5 px-2">
                      <Input value={issue.owner} onChange={(e) => updateIssue(idx, "owner", e.target.value)} className="h-7 text-xs" data-testid={`qc-issue-owner-${idx}`} />
                    </td>
                    <td className="py-1.5 px-2">
                      <Input value={issue.mitigation} onChange={(e) => updateIssue(idx, "mitigation", e.target.value)} className="h-7 text-xs" data-testid={`qc-issue-mitigation-${idx}`} />
                    </td>
                    <td className="py-1.5 px-2">
                      <select value={issue.impact} onChange={(e) => updateIssue(idx, "impact", e.target.value)}
                        className={`h-7 w-full text-center rounded text-xs font-bold border ${issue.impact === "H" ? "bg-red-50 text-red-700 border-red-200" : issue.impact === "M" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-green-50 text-green-700 border-green-200"}`}
                        data-testid={`qc-issue-impact-${idx}`}
                      >
                        <option value="H">H</option>
                        <option value="M">M</option>
                        <option value="L">L</option>
                      </select>
                    </td>
                    <td className="py-1.5 px-2">
                      <input type="date" value={issue.due_date} onChange={(e) => updateIssue(idx, "due_date", e.target.value)}
                        className="h-7 w-full px-1.5 rounded text-xs border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-purple-500/30" data-testid={`qc-issue-date-${idx}`} />
                    </td>
                    <td className="py-1.5 px-1">
                      <button onClick={() => removeIssue(idx)} className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50"><Trash2 className="w-3 h-3" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button onClick={addIssue} variant="outline" size="sm" className="mt-3 h-7 text-xs" data-testid="qc-add-issue">
            <Plus className="w-3 h-3 mr-1" />Add Issue/Risk
          </Button>
        </CardContent>
      </Card>

      {/* Status Legend */}
      <div className="flex items-center gap-4 mt-4 px-2" data-testid="qc-legend">
        {statusOptions.map((s) => (
          <div key={s.value} className="flex items-center gap-1.5">
            <div className={`w-4 h-4 rounded-full ${s.color} flex items-center justify-center`}>
              <span className="text-[8px] font-bold text-white">{s.value}</span>
            </div>
            <span className="text-[10px] text-slate-500 font-medium">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ─── Add New Month Report ─── */}
      <div className="mt-8 pt-6 border-t border-dashed border-slate-200 dark:border-slate-700">
        <div className="flex flex-col items-center gap-3">
          <p className="text-xs text-slate-400 dark:text-slate-500">New reports will carry over data from the currently displayed month</p>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => requestCreateMonth()}
              className="bg-[#00D4C8] hover:bg-[#00B8AE] text-white font-semibold px-6 py-2.5 rounded-xl shadow-md hover:shadow-lg transition-all"
              data-testid="add-new-month-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Next Month
            </Button>
            <Popover open={showNewMonthPicker} onOpenChange={setShowNewMonthPicker}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="font-semibold px-4 py-2.5 rounded-xl border-slate-300 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                  data-testid="pick-month-btn"
                >
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Pick Month
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-4 dark:bg-slate-800 dark:border-slate-700" align="center">
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Select target month</p>
                <Input
                  type="month"
                  value={targetMonth}
                  onChange={(e) => setTargetMonth(e.target.value)}
                  className="mb-3 dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                  data-testid="month-picker-input"
                />
                <Button
                  onClick={() => { if (targetMonth) { requestCreateMonth(targetMonth); setShowNewMonthPicker(false); } }}
                  disabled={!targetMonth || availableMonths.includes(targetMonth)}
                  className="w-full bg-[#00D4C8] hover:bg-[#00B8AE] text-white"
                  data-testid="create-month-btn"
                >
                  {availableMonths.includes(targetMonth) ? "Already Exists" : "Create Report"}
                </Button>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {/* ─── Confirmation Dialog ─── */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <DialogContent className="sm:max-w-md dark:bg-slate-900 dark:border-slate-700" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-slate-100">
              {confirmAction?.type === "delete" && <AlertTriangle className="w-5 h-5 text-red-500" />}
              {confirmAction?.type === "create" ? "Create New Report" : "Delete Report"}
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              {confirmAction?.type === "create"
                ? `This will create a new report for ${confirmAction?.label} using data from the currently displayed month as a template.`
                : `Are you sure you want to permanently delete the ${confirmAction?.label} report? This action cannot be undone.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmAction(null)} className="dark:border-slate-600 dark:text-slate-300" data-testid="confirm-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              className={confirmAction?.type === "delete" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[#00D4C8] hover:bg-[#00B8AE] text-white"}
              data-testid="confirm-ok-btn"
            >
              {confirmAction?.type === "create" ? "Create Report" : "Delete Report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
